import type { IAllocationHistoryExportCmd } from "@/data/commands/table/AllocationHistoryExportCmd";
import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { TableDao } from "@/data/dao/service/TableDao";
import type { ILocation } from "@/data/model/Location";
import type { ITable } from "@/data/model/Table";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { DatePicker } from "@/portal/formControls/DatePicker";
import { Select } from "@/portal/formControls/Select";
import { UiNotification } from "@/portal/ui/Notification";

export class AllocationHistoryExportWindow extends BaseModal {
    private $form: JQuery;
    private $submitBtn: JQuery;

    private locationSelect: Select<ILocation>;
    private tableSelect: Select<ITable>;
    private fromPicker: DatePicker;
    private toPicker: DatePicker;

    constructor() {
        super(i18n("modal.allocationHistoryExport.title"), "modal-lg", false);

        this.$element.addClass("allochistoryexport");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.allocationHistoryExport.settings") +
                "</h3></div></div>"
        );

        const roomGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.location") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.locationSelect = new Select<ILocation>(
            undefined,
            Factory.getInstance(LocationDao),
            (c) => c.name,
            false,
            i18n("modal.allocationHistoryExport.selectLocation")
        ).renderTo(roomGroup);
        this.locationSelect.onChange = () => this.refreshTables();

        const tableGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.table") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.tableSelect = new Select<ITable>(
            undefined,
            {
                getData: () => {
                    return new Promise((resolve) => {
                        const locationId = this.locationSelect.getValue();
                        if (locationId === undefined) {
                            resolve({
                                items: [],
                                limit: 0,
                                offset: 0,
                                hasNext: false,
                                hasPrev: false,
                            });
                            return;
                        }

                        return Factory.getInstance<TableDao>(TableDao)
                            .getData({ locationId: locationId, orderBy: "name" })
                            .then((s) => {
                                resolve(s);
                            });
                    });
                },
            },
            (c) => c.name,
            true,
            i18n("modal.allocationHistoryExport.selectTable")
        ).renderTo(tableGroup);

        const fromToGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.allocationHistoryExport.fromTo") +
                '</label><div class="col-xl-3 from"></div><div class="col-xl-3 to"></div></div>'
        ).appendTo(createSection);
        this.fromPicker = new DatePicker(undefined, { endDate: "today" }).renderTo(
            fromToGroup.find("> .col-xl-3.from")
        );
        this.toPicker = new DatePicker(undefined, { endDate: "today" }).renderTo(fromToGroup.find("> .col-xl-3.to"));

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.export") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onExport();

            return false;
        });
    }

    protected onShow() {}

    protected onHide() {}

    private refreshTables() {
        this.tableSelect.reset();
        this.tableSelect.fetchData();
    }

    private onExport() {
        const data: IAllocationHistoryExportCmd = {
            locationId: this.locationSelect.getValue()!,
            tableId: this.tableSelect.getValue(),
            from: this.fromPicker.getValue(),
            to: this.toPicker.getValue(),
        };

        let hasError = false;
        if (data.locationId === undefined) {
            this.locationSelect.setError(true);
            hasError = true;
        } else this.locationSelect.setError(false);

        if (data.from !== undefined && data.to !== undefined && data.from > data.to) {
            this.fromPicker.setError(true);
            this.toPicker.setError(true);
            hasError = true;
        } else {
            this.fromPicker.setError(false);
            this.toPicker.setError(false);
        }

        if (hasError) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        Factory.getInstance<TableDao>(TableDao)
            .exportTableAllocationHistory(data)
            .then(() => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                this.close();
            })
            .catch((ex) => {
                logError(ex);
                UiNotification.showException(i18n("modal.allocationHistoryExport.failedToExport"));
            });
    }
}
